import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Input, InputNumber, Space, Popconfirm, message, Col, Row, Switch } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { useQuery, useMutation } from '@apollo/client';
import {
  GET_PAYMENT_METHODS,
  CREATE_PAYMENT_METHOD,
  UPDATE_PAYMENT_METHOD,
  DELETE_PAYMENT_METHOD
} from '../queries/OnsiteDeliveryQueries';
import './styles.css';
import { useHistory } from 'react-router-dom';

const ConfiOnsiteDelivery = () => {
  const history = useHistory();
  const { data, loading, refetch } = useQuery(GET_PAYMENT_METHODS);
  const [createPaymentMethod] = useMutation(CREATE_PAYMENT_METHOD);
  const [updatePaymentMethod] = useMutation(UPDATE_PAYMENT_METHOD);
  const [deletePaymentMethod] = useMutation(DELETE_PAYMENT_METHOD);

  const [modalVisible, setModalVisible] = useState(false);
  const [editing, setEditing] = useState(false);
  const [currentRecord, setCurrentRecord] = useState(null);
  const [form] = Form.useForm();
  const [selectedRowKey, setSelectedRowKey] = useState("");

  useEffect(() => {
    if (editing && modalVisible && currentRecord) {
      form.setFieldsValue(currentRecord);
    }
  }, [editing, modalVisible, currentRecord, form]);

  const showAddModal = () => {
    setEditing(false);
    setCurrentRecord(null);
    form.resetFields();
    setModalVisible(true);
  };

  const showEditModal = (record) => {
    setEditing(true);
    setCurrentRecord(record);
    form.setFieldsValue(record);
    setModalVisible(true);
  };

  const handleCancel = () => {
    setModalVisible(false);
    setCurrentRecord(null);
    form.resetFields();
  };

  const handleDelete = async (id) => {
    try {
      await deletePaymentMethod({ variables: { id } });
      message.success('Método de pago eliminado correctamente');
      refetch();
    } catch (error) {
      message.error('Error al eliminar el método de pago');
    }
  };

  const handleFinish = async (values) => {
    try {
      if (editing && currentRecord) {
        await updatePaymentMethod({ variables: { id: values.id, changes: {
          paymentMethod: values.paymentMethod,
          minimumAcceptedValue: values.minimumAcceptedValue,
          maximumAcceptedValue: values.maximumAcceptedValue,
          disabledButtons: values.disabledButtons,
          showAlert: values.showAlert,
          alertMessage: values.alertMessage
        }} });
        message.success('Método de pago actualizado correctamente');
      } else {
        await createPaymentMethod({ variables: { object: values } });
        message.success('Método de pago creado correctamente');
      }
      setModalVisible(false);
      form.resetFields();
      refetch();
    } catch (error) {
      message.error('Error al guardar el método de pago');
    }
  };

  const handleRowClick = (record) => {
    if (!selectedRowKey || selectedRowKey !== record.id) {
      setSelectedRowKey(record.id);
    } else {
      setSelectedRowKey("");
    }
  };

  const handleEditButton = () => {
    const record = (data?.confOnSiteDeliveries || []).find(item => item.id === selectedRowKey);
    if (record) {
      showEditModal(record);
    }
  };

  const handleDeleteButton = () => {
    if (selectedRowKey) {
      Modal.confirm({
        title: '¿Estás seguro que deseas eliminar este método de pago?',
        content: 'Esta acción no se puede deshacer.',
        okText: 'Sí, eliminar',
        okType: 'danger',
        cancelText: 'Cancelar',
        onOk() {
          handleDelete(selectedRowKey);
        },
      });
    }
  };


  const handleCreateButton = () => {
    showAddModal();
  };

  const columns = [
    {
      title: 'Método de Pago',
      dataIndex: 'paymentMethod',
      key: 'paymentMethod',
    },
    {
      title: 'Valor Mínimo Aceptado',
      dataIndex: 'minimumAcceptedValue',
      key: 'minimumAcceptedValue',
    },
    {
      title: 'Valor Máximo Aceptado',
      dataIndex: 'maximumAcceptedValue',
      key: 'maximumAcceptedValue',
    },
    {
      title: 'Habilitado',
      dataIndex: 'disabledButtons',
      key: 'disabledButtons',
      align: 'center',
      render: (value) => <Switch checked={!value} disabled />
    },
    {
      title: 'Mostrar Alerta',
      dataIndex: 'showAlert',
      key: 'showAlert',
      align: 'center',
      render: (value) => <Switch checked={value} disabled />
    },
    {
      title: 'Mensaje de Alerta',
      dataIndex: 'alertMessage',
      key: 'alertMessage',
      render: (text, record) => record.showAlert ? text : null,
      className: 'alert-message-justified'
    },
  ];

  return (
    <Col className='children-inner-container-with-bg'>
      <Button
        type="default"
        shape="round"
        style={{
          color: 'var(--primaryColor)',
          border: 'none',
          fontWeight: 'normal',
          fontSize: 15,
          background: '#fff',
          boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
          marginBottom: 16
        }}
        icon={<ArrowLeftOutlined />}
        onClick={() => history.push('/configuration')}
      >
        Configuración
      </Button>
      
      <Row className='children-inner-row' justify="end">
        <Col>
          <Space size="middle">
            <Button type="primary" size="large" className='children-inner-button' icon={<PlusOutlined />} onClick={handleCreateButton}>
              Crear
            </Button>
            <Button type="primary" size="large" className='children-inner-button' icon={<EditOutlined />} onClick={handleEditButton} disabled={!selectedRowKey}>
              Editar
            </Button>
            <Button type="primary" size="large" className='children-inner-button' icon={<DeleteOutlined />} onClick={handleDeleteButton} disabled={!selectedRowKey} danger>
              Eliminar
            </Button>
          </Space>
        </Col>
      </Row>
      <Table
        columns={columns}
        dataSource={data?.confOnSiteDeliveries || []}
        loading={loading}
        rowKey="id"
        pagination={{ pageSize: 10 }}
        className='children-table'
        size='small'
        onRow={(record) => ({
          onClick: () => handleRowClick(record),
        })}
        rowClassName={(record) => ['mouse-row-enter', record.id === selectedRowKey ? 'table-row-color' : ""]}
        style={{ marginLeft: 'auto', marginRight: 'auto', maxWidth: '95%' }}
      />
      <Modal
        title={editing ? 'Editar Método de Pago' : 'Nuevo Método de Pago'}
        visible={modalVisible}
        onCancel={handleCancel}
        footer={null}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleFinish}
        >
          <Form.Item name="id" hidden>
            <Input />
          </Form.Item>
          <Form.Item
            label="Método de Pago"
            name="paymentMethod"
            rules={[{ required: true, message: 'Ingrese el método de pago' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            label="Valor Mínimo Aceptado"
            name="minimumAcceptedValue"
            rules={[{ required: true, message: 'Ingrese el valor mínimo aceptado' }]}
          >
            <InputNumber style={{ width: '100%' }} min={0} step={0.01} />
          </Form.Item>
          <Form.Item
            label="Valor Máximo Aceptado"
            name="maximumAcceptedValue"
            rules={[{ required: true, message: 'Ingrese el valor máximo aceptado' }]}
          >
            <InputNumber style={{ width: '100%' }} min={0} step={0.01} />
          </Form.Item>
          <Form.Item
            label="Deshabilitado"
            name="disabledButtons"
            valuePropName="checked"
          >
            <Switch checkedChildren="Sí" unCheckedChildren="No" />
          </Form.Item>
          <Form.Item
            label="Mostrar Alerta"
            name="showAlert"
            valuePropName="checked"
          >
            <Switch checkedChildren="Sí" unCheckedChildren="No" />
          </Form.Item>

          {/* Campo condicional para alertMessage */}
          <Form.Item shouldUpdate={(prev, curr) => prev.showAlert !== curr.showAlert}>
            {({ getFieldValue }) =>
              getFieldValue('showAlert') ? (
                <Form.Item
                  label="Mensaje de Alerta"
                  name="alertMessage"
                  rules={[{ required: true, message: 'Ingrese el mensaje de alerta' }]}
                >
                  <Input />
                </Form.Item>
              ) : null
            }
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {editing ? 'Actualizar' : 'Crear'}
              </Button>
              <Button onClick={handleCancel}>
                Cancelar
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </Col>
  );
};

export default ConfiOnsiteDelivery;
